import React, { useEffect, useState, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ticketsApi, telegramApi } from '../services/api';
import { toast } from 'react-hot-toast';
import ws from '../services/wsService';
import { useAuthStore } from '../stores/authStore';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { StatusBadge } from '../components/common/StatusBadge';
import { Skeleton } from '../components/ui/skeleton';
import EscalationPanel from '../components/EscalationPanel';
import InternalChat from '../components/InternalChat';
import CsatBadge from '../components/CsatBadge';
import {
  ChatBubbleLeftRightIcon,
  ArrowPathIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import api from '../services/api';

// Media (photo/video/audio/document) is served by the Telegram proxy ONLY via
// a signed, expiring URL that the backend puts into `mediaUrl` (API and WS
// payloads alike). Fall back to the bare path for stale in-memory messages.
/** Подпись-разделитель дня, если у сообщения другой день, чем у предыдущего. */
const dayLabelFor = (ts?: string, prevTs?: string): string | null => {
  if (!ts) return null;
  const d = new Date(ts);
  if (prevTs && new Date(prevTs).toDateString() === d.toDateString()) return null;
  const today = new Date();
  const yesterday = new Date(); yesterday.setDate(today.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Сегодня';
  if (d.toDateString() === yesterday.toDateString()) return 'Вчера';
  return d.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', ...(d.getFullYear() !== today.getFullYear() ? { year: 'numeric' } : {}) });
};

const mediaSrc = (msg: any): string =>
  msg?.mediaUrl || `/api/telegram/media/${msg?.telegramFilePath}`;


interface TicketDetailPageProps {
  // When rendered inside the Workspace (left-rail + center layout) the ticket
  // id is passed explicitly instead of coming from the route params. Falls back
  // to the URL param so the standalone /tickets/:id page is unchanged.
  ticketId?: string;
  // Hide the in-page "Открыть в рабочем окне" shortcut when we're ALREADY in
  // the workspace (the prop is supplied), to avoid a link to ourselves.
  embedded?: boolean;
  /** 'chat' — только лента + композер (Рабочее окно в стиле Telegram). */
  variant?: 'card' | 'chat';
  /** Внешнее управление вкладкой (чат с клубом / внутренний чат). */
  tab?: 'messages' | 'internal';
}

const TicketDetailPage: React.FC<TicketDetailPageProps> = ({ ticketId, embedded, variant = 'card', tab }) => {
  const params = useParams<{ id: string }>();
  const id = ticketId ?? params.id;
  const [ticket, setTicket] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  // Pasted-image preview: hold the blob + an object URL so the operator can
  // eyeball the screenshot (and add a caption) BEFORE it's sent — prevents
  // firing off the wrong screenshot by accident, like Telegram's send-preview.
  const [pendingImage, setPendingImage] = useState<{ blob: Blob; url: string; name: string } | null>(null);
  const [pendingCaption, setPendingCaption] = useState('');
  // Single-flight guard for the status-change buttons (Pause / Resume /
  // Решено / Закрыть). Without this, hitting a button while the request is
  // in flight just looks like "ничего не происходит" — the user double-clicks
  // and fires the same transition twice. Disables every action button while
  // any one is mid-request.
  const [statusUpdating, setStatusUpdating] = useState(false);
  // Single operator-set label «Установка/обновление игр» — drives the
  // show-only / hide filter on the tickets list.
  const [flagUpdating, setFlagUpdating] = useState(false);
  // Lightbox for inline photos/stickers — chat bubbles are tiny (max-h-60/80),
  // so a tap on the image opens it full-screen for actual readability.
  const [lightboxSrc, setLightboxSrc] = useState<string | null>(null);
  // Esc closes the lightbox + lock body scroll while it's open.
  useEffect(() => {
    if (!lightboxSrc) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setLightboxSrc(null);
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [lightboxSrc]);
  const [activeTab, setActiveTab] = useState<'messages' | 'internal' | 'transfers' | 'worktime'>('messages');
  useEffect(() => { if (tab) setActiveTab(tab); }, [tab]);
  const [workTimeMetrics, setWorkTimeMetrics] = useState<any>(null);
  const [workTimeLoading, setWorkTimeLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  // Поле ввода растёт с текстом (3 строки по умолчанию, до ~8), фокус
  // возвращается в него после отправки/вложения — «как в Telegram».
  const autosize = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 220)}px`;
  };
  const refocus = () => { setTimeout(() => textareaRef.current?.focus(), 0); };
  const currentTicketIdRef = useRef<string | null>(null);
  const currentChatIdRef = useRef<string | null>(null);
  const sliceRefetchTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const { user } = useAuthStore();
  const navigate = useNavigate();

  useEffect(() => {
    if (id) {
      fetchTicket(id);
    }
  }, [id]);

  // Put the club/client name in the browser tab title so a ticket opened in a
  // new tab (middle-click from the list) is identifiable at a glance. Restore
  // the default title on unmount.
  useEffect(() => {
    if (!ticket) return;
    const club = ticket.telegramChatType === 'private'
      ? (ticket.telegramUsername
          ? `@${ticket.telegramUsername}`
          : `${ticket.telegramFirstName || ''} ${ticket.telegramLastName || ''}`.trim())
      : (ticket.telegramChatTitle || 'Заявка');
    const num = ticket.ticketNumber ? `#${ticket.ticketNumber}` : '';
    document.title = `${club} ${num}`.trim() || 'GGDesk';
    return () => { document.title = 'GGDesk CRM'; };
  }, [ticket?.id, ticket?.telegramChatTitle, ticket?.telegramUsername, ticket?.ticketNumber]);

  useEffect(() => {
    if (activeTab === 'messages' && ticket?.messages?.length) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [activeTab, ticket?.messages?.length]);

  // Подписка на комнату заявки через глобальный wsService
  useEffect(() => {
    if (!id) return;

    currentTicketIdRef.current = id;
    ws.joinTicket(id);

    const handleNewMessage = (payload: any) => {
      const payloadTicketId = String(payload.ticketId || '');
      const currentTicketId = String(currentTicketIdRef.current || '');

      if (payloadTicketId === currentTicketId) {
        setTicket((prev: any) => {
          if (!prev) return prev;

          // Dedup by stable id. When a payload with an id that already
          // exists arrives, it's either a duplicate (ignore) or an EDIT
          // (replace the content of the existing message in place — without
          // this, edits from Telegram never updated the rendered text).
          const incomingId = payload.message?.id;
          const isEdit = !!payload.message?.edited;
          if (incomingId) {
            const existsAt = (prev.messages || []).findIndex(
              (msg: any) => msg.id === incomingId
            );
            if (existsAt >= 0) {
              if (!isEdit) return prev;
              const next = [...prev.messages];
              next[existsAt] = { ...next[existsAt], ...payload.message };
              return { ...prev, messages: next };
            }
            return {
              ...prev,
              messages: [...(prev.messages || []), payload.message],
            };
          }

          // Legacy fallback for payloads without id: dedup by
          // content+sender+timestamp window (<2s).
          const messageExists = prev.messages?.some((msg: any) => {
            const sameContent = msg.content === payload.message?.content;
            const sameSender = msg.sender === payload.message?.sender;
            const msgTimestamp = msg.timestamp ? new Date(msg.timestamp).getTime() : null;
            const payloadTimestamp = payload.message?.timestamp
              ? new Date(payload.message.timestamp).getTime()
              : null;
            if (msgTimestamp && payloadTimestamp) {
              const timeDiff = Math.abs(msgTimestamp - payloadTimestamp);
              return timeDiff < 2000 && sameContent && sameSender;
            }
            return false;
          });

          if (messageExists) return prev;

          return {
            ...prev,
            messages: [...(prev?.messages || []), payload.message],
          };
        });
      }
    };

    const handleTicketUpdated = (payload: any) => {
      const payloadTicketId = String(payload.ticketId || '');
      const currentTicketId = String(currentTicketIdRef.current || '');
      if (payloadTicketId === currentTicketId) {
        fetchTicket(currentTicketId);
        return;
      }
      // Вырезка чата: сообщение пришло в ДРУГУЮ заявку этого же клуба — оно
      // входит и в нашу ленту, перечитываем (с дебаунсом).
      if (payload.chatId && currentChatIdRef.current && String(payload.chatId) === String(currentChatIdRef.current)) {
        if (sliceRefetchTimer.current) clearTimeout(sliceRefetchTimer.current);
        sliceRefetchTimer.current = setTimeout(() => { sliceRefetchTimer.current = null; fetchTicket(currentTicketId); }, 600);
      }
    };

    ws.on('new_message', handleNewMessage);
    ws.on('ticket_updated', handleTicketUpdated);

    return () => {
      ws.off('new_message', handleNewMessage);
      ws.off('ticket_updated', handleTicketUpdated);
      ws.leaveTicket(id);
    };
  }, [id]);

  const fetchTicket = async (ticketId: string) => {
    try {
      const response = await ticketsApi.getById(ticketId);
      const apiData = response.data?.data?.ticket || response.data?.ticket || response.data;
      currentChatIdRef.current = apiData?.telegramChatId || null;
      
      // Merge incoming-from-API messages with whatever the WS handler may
      // have just pushed into `prev`. The previous version of this code
      // keyed on `timestamp`, but Django's `auto_now_add` rewrites the
      // timestamp at save-time — so the WS payload (built BEFORE save) and
      // the API response (read AFTER save) carry timestamps that differ by
      // microseconds. Keying on timestamp therefore failed to dedupe and
      // every incoming Telegram message rendered twice until a hard refresh.
      // Stable `id` (UUID) is present on both sides and is the right key.
      setTicket((prev: any) => {
        if (!apiData) return apiData;

        if (prev && prev.messages && apiData.messages) {
          const byId = new Map<string, any>();
          const pushUnique = (msg: any) => {
            const key = msg?.id
              ? `id:${msg.id}`
              // Fallback for ancient payloads without id — same heuristic
              // the new_message handler uses as its backup.
              : `fb:${msg?.content || ''}|${msg?.sender || ''}|${msg?.messageType || 'text'}|${msg?.timestamp || ''}`;
            if (!byId.has(key)) byId.set(key, msg);
          };
          // API first → wins for any id that exists on both sides. This is
          // what makes edits visible: when content changed server-side,
          // apiData carries the new text, prev still has the old.
          // prev is only consulted for messages that aren't in apiData yet
          // (a WS-just-arrived row that the fetch crossed in flight).
          apiData.messages.forEach(pushUnique);
          prev.messages.forEach(pushUnique);

          const allMessages = Array.from(byId.values()).sort((a: any, b: any) => {
            const timeA = a.timestamp ? new Date(a.timestamp).getTime() : 0;
            const timeB = b.timestamp ? new Date(b.timestamp).getTime() : 0;
            return timeA - timeB;
          });

          return {
            ...apiData,
            messages: allMessages
          };
        }

        return apiData;
      });
      
      // Загружаем метрики времени работы
      if (apiData) {
        fetchWorkTimeMetrics(ticketId);
      }
    } catch (error) {
      toast.error('Ошибка загрузки заявки');
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkTimeMetrics = async (ticketId: string) => {
    try {
      setWorkTimeLoading(true);
      const response = await api.get(`/api/tickets/${ticketId}/work-time`);
      if (response.data.success) {
        setWorkTimeMetrics(response.data.data);
      }
    } catch (error: any) {
      // Игнорируем ошибки, если нет доступа
      if (error.response?.status !== 403) {
        console.error('Error fetching work time metrics:', error);
      }
    } finally {
      setWorkTimeLoading(false);
    }
  };

  // handleWorkStart / handleWorkPause / handleWorkResume removed — the
  // work-time clock is now driven by the ticket status itself
  // (in_progress = running, anything else = stopped). The Pause/Resume header
  // buttons trigger that via handleStatusChange.

  const formatDuration = (seconds: number) => {
    if (!seconds || isNaN(seconds)) return '0 сек';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}ч ${minutes}м ${secs}с`;
    } else if (minutes > 0) {
      return `${minutes}м ${secs}с`;
    } else {
      return `${secs}с`;
    }
  };

  // Функция для нормализации статуса
  const normalizeStatus = (status: string) => {
    return (status || '').replace(/-/g, '_');
  };

  const handleStatusChange = async (status: string) => {
    if (!id) return;
    
    // Нормализуем статус для сравнения (заменяем дефисы на подчеркивания)
    const normalizedStatus = normalizeStatus(status);
    const currentStatus = normalizeStatus(ticket.status || '');
    
    // Проверяем, не пытаемся ли установить тот же статус
    if (currentStatus === normalizedStatus) {
      toast.error('Заявка уже имеет этот статус');
      return;
    }
    
    // `resolved` and `closed` both require a short explanation note —
    // it's what the client sees and what an auditor reads later.
    // Other transitions (paused / in_progress) go through without a prompt.
    const NOTE_PROMPTS: Record<string, { ask: string; missing: string; success: string }> = {
      resolved: {
        ask: 'Опишите коротко, что было сделано по заявке:',
        missing: 'Нужно указать, что вы сделали',
        success: 'Заявка отмечена как решённая — клиенту отправлен опрос CSAT',
      },
      closed: {
        ask: 'Укажите причину закрытия заявки:',
        missing: 'Необходимо указать причину закрытия заявки',
        success: 'Заявка закрыта',
      },
    };

    let note: string | undefined;
    if (NOTE_PROMPTS[status]) {
      const cfg = NOTE_PROMPTS[status];
      const value = window.prompt(cfg.ask);
      if (!value || value.trim() === '') {
        toast.error(cfg.missing);
        return;
      }
      note = value.trim();
    }

    // Loading toast gives immediate feedback so the operator doesn't think
    // the button is dead while the API call (and any backend work) runs.
    const toastId = toast.loading('Обновляем статус…');
    try {
      setStatusUpdating(true);
      await ticketsApi.updateStatus(id, status, note);
      setTicket({ ...ticket, status });
      toast.success(NOTE_PROMPTS[status]?.success || 'Статус обновлен', { id: toastId });
      fetchTicket(id);
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || 'Ошибка обновления статуса';
      toast.error(errorMessage, { id: toastId });
    } finally {
      setStatusUpdating(false);
    }
  };


  const toggleGameInstall = async () => {
    if (!id || flagUpdating) return;
    const next = !(ticket as any).isGameInstall;
    setFlagUpdating(true);
    try {
      await ticketsApi.updateFlags(id, { isGameInstall: next });
      setTicket((p: any) => (p ? { ...p, isGameInstall: next } : p));
      toast.success(next ? 'Метка «Установка игр» поставлена' : 'Метка снята');
    } catch {
      toast.error('Не удалось обновить метку');
    } finally {
      setFlagUpdating(false);
    }
  };

  // Reusable label toggle chip — rendered in both the compact (embedded) and
  // classic headers.
  const gameInstallChip = (
    <button
      type="button"
      onClick={toggleGameInstall}
      disabled={flagUpdating}
      title="Пометить заявку как «Установка/обновление игр» (для фильтра в списке)"
      className={`inline-flex items-center gap-1 text-xs px-2.5 py-1 rounded-full border transition-colors disabled:opacity-60 ${
        (ticket as any)?.isGameInstall
          ? 'bg-blue-500/10 text-blue-600 border-blue-500/30'
          : 'text-muted-foreground border-border hover:bg-accent'
      }`}
    >
      🎮 Установка/обновление игр{(ticket as any)?.isGameInstall ? ' ✓' : ''}
    </button>
  );

  const handleSendMessage = async (e: React.FormEvent | React.KeyboardEvent) => {
    e.preventDefault();
    if (!id || !message.trim() || sendingMessage) return;

    try {
      setSendingMessage(true);
      // Отправляем сообщение в Telegram через backend
      await telegramApi.sendMessage(ticket.telegramChatId, message, id);
      setMessage('');
      setTimeout(autosize, 0);
      refocus();
      // Не вызываем fetchTicket, так как сообщение придет через WebSocket
      // Это предотвращает дублирование сообщений
      toast.success('Сообщение отправлено');
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Ошибка отправки сообщения в Telegram');
      // В случае ошибки обновляем тикет, чтобы увидеть актуальное состояние
      fetchTicket(id);
    } finally {
      setSendingMessage(false);
    }
  };

  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
    if (!id) return;
    const files = e.target.files;
    if (!files || !files.length) return;
    const list = Array.from(files);
    try {
      // Несколько файлов — по очереди, одним действием оператора.
      for (const f of list) await telegramApi.upload(id, f);
      toast.success(list.length > 1 ? `Отправлено файлов: ${list.length}` : 'Файл отправлен');
    } catch (err) {
      toast.error('Ошибка отправки файла');
    } finally {
      e.target.value = '';
      refocus();
    }
  };

  const handlePaste: React.ClipboardEventHandler<HTMLTextAreaElement> = (e) => {
    if (!id) return;
    const items = e.clipboardData?.items || [];
    for (let i = 0; i < items.length; i++) {
      const it = items[i];
      if (it.kind === 'file' && it.type.startsWith('image/')) {
        const blob = it.getAsFile();
        if (blob) {
          // Don't paste the binary into the textarea, and don't send yet —
          // show a preview so the operator can confirm it's the right shot.
          e.preventDefault();
          const ext = (blob.type.split('/')[1] || 'png').replace('jpeg', 'jpg');
          const url = URL.createObjectURL(blob);
          setPendingImage((prev) => {
            if (prev) URL.revokeObjectURL(prev.url);
            return { blob, url, name: `pasted.${ext}` };
          });
          setPendingCaption('');
        }
        break;
      }
    }
  };

  const cancelPendingImage = () => {
    setPendingImage((prev) => {
      if (prev) URL.revokeObjectURL(prev.url);
      return null;
    });
    setPendingCaption('');
  };

  const sendPendingImage = async () => {
    if (!id || !pendingImage || sendingMessage) return;
    setSendingMessage(true);
    try {
      await telegramApi.upload(id, pendingImage.blob, pendingImage.name);
      // Optional caption goes as a separate text message right after, so it
      // shows up in both the CRM thread and the client's chat.
      const caption = pendingCaption.trim();
      if (caption) {
        await telegramApi.sendMessage(ticket.telegramChatId, caption, id);
      }
      toast.success('Изображение отправлено');
      cancelPendingImage();
    } catch (err) {
      toast.error('Ошибка отправки изображения');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleDrop: React.DragEventHandler<HTMLDivElement> = async (e) => {
    e.preventDefault();
    const files = e.dataTransfer.files;
    if (!files || !files.length || !id) return;
    
    const list = Array.from(files);
    try {
      for (const f of list) await telegramApi.upload(id, f);
      toast.success(list.length > 1 ? `Отправлено файлов: ${list.length}` : 'Файл отправлен');
      fetchTicket(id);
    } catch (err) {
      toast.error('Ошибка отправки файла');
    } finally {
      refocus();
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    
    if (!window.confirm('Вы уверены, что хотите удалить эту заявку? Это действие нельзя отменить.')) {
      return;
    }
    
    try {
      await ticketsApi.delete(id);
      toast.success('Заявка успешно удалена');
      navigate('/tickets');
    } catch (error) {
      toast.error('Ошибка при удалении заявки');
    }
  };

  const preventDefault: React.DragEventHandler<HTMLDivElement> = (e) => {
    e.preventDefault();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center space-y-4">
          <Skeleton className="h-12 w-12 rounded-full" />
          <Skeleton className="h-4 w-32" />
        </div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-foreground">Заявка не найдена</h3>
      </div>
    );
  }

  // ── Переиспользуемые куски: лента сообщений и композер. Рендерятся и в
  // классической карточке, и в режиме «чат» (Рабочее окно в стиле Telegram).
  const chatMode = variant === 'chat';
  const feedBody = (
              <div className={chatMode ? 'flex-1 min-h-0 overflow-y-auto px-3 py-3 space-y-1.5' : 'space-y-3 sm:space-y-4 max-h-[60vh] overflow-y-auto bg-card'} onDrop={handleDrop} onDragOver={preventDefault}>
                {ticket.messages?.map((msg: any, index: number) => {
                  const isOperator = msg.sender === 'operator';
                  // Prefer the senderName stored on the message itself — it reflects who
                  // actually wrote it, not the *current* assignee (which may have changed).
                  // Also fixes "undefined undefined" when the legacy code did
                  // `${ticket.assignedTo.firstName} ${ticket.assignedTo.lastName}` on a
                  // payload where assignedTo was just an ID string.
                  const name = msg.senderName || (isOperator ? 'Оператор' : 'Клиент');
                  // Telegram-like feed: «Система» notices as centered service
                  // pills, a date separator whenever the day changes.
                  const isSystem = msg.senderName === 'Система';
                  // Вырезка чата: сообщение другой заявки этого клуба — для
                  // контекста, приглушено и подписано номером.
                  const foreign = !!msg.foreign;
                  const foreignNum = String(msg.ticketNumber || '').replace(/^TKT-\d{4}-0*/, '');
                  const prevMsg: any = index > 0 ? ticket.messages?.[index - 1] : null;
                  const dayLabel = dayLabelFor(msg.timestamp, prevMsg?.timestamp);
                  return (
                    <React.Fragment key={msg.id || index}>
                    {dayLabel && (
                      <div className="flex justify-center my-2">
                        <span className="px-3 py-0.5 rounded-full bg-muted text-[11px] text-muted-foreground">{dayLabel}</span>
                      </div>
                    )}
                    {isSystem ? (
                      <div className="flex justify-center">
                        <span className="max-w-[90%] text-center px-3 py-1 rounded-2xl bg-muted/70 text-[11px] text-muted-foreground whitespace-pre-wrap break-words">
                          {msg.content}
                        </span>
                      </div>
                    ) : (
                    <div className={`flex ${isOperator ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[85%] sm:max-w-[70%] px-3 sm:px-4 py-2 shadow ${isOperator ? 'bg-primary text-primary-foreground rounded-2xl rounded-br-md' : 'bg-muted text-foreground rounded-2xl rounded-bl-md'}${foreign ? ' opacity-60' : ''}`}>
                        <div className="flex items-center gap-1 text-xs font-semibold opacity-80 mb-1">
                          <span>{name}</span>
                          {foreign && <span className="text-[10px] font-normal opacity-80" title="Сообщение другой заявки этого клуба">· заявка {foreignNum}</span>}
                          {msg.edited && <span className="text-[10px] opacity-70">(изменено)</span>}
                        </div>

                        {/* Reply badge — shown above the bubble's main content
                            when this message replies to another. Renders a
                            short preview so the operator instantly sees
                            context, not just orphan text. */}
                        {msg.replyTo && (
                          <div className={`border-l-2 pl-2 mb-2 text-xs opacity-90 ${isOperator ? 'border-primary-foreground/60' : 'border-primary'}`}>
                            <div className="font-medium truncate">
                              ↪ {msg.replyTo.senderName || (msg.replyTo.sender === 'operator' ? 'Оператор' : 'Клиент')}
                            </div>
                            <div className="truncate opacity-80">
                              {msg.replyTo.messageType && msg.replyTo.messageType !== 'text'
                                ? `[${msg.replyTo.messageType}]${msg.replyTo.content ? ' ' + msg.replyTo.content : ''}`
                                : (msg.replyTo.content || '...')}
                            </div>
                          </div>
                        )}

                        {msg.messageType === 'photo' && msg.telegramFilePath && (
                          <img
                            src={mediaSrc(msg)}
                            alt={msg.fileName || 'photo'}
                            className="rounded mb-2 max-h-60 sm:max-h-80 w-full cursor-zoom-in"
                            onClick={() => setLightboxSrc(mediaSrc(msg))}
                          />
                        )}
                        {msg.messageType === 'sticker' && msg.telegramFilePath && (
                          <img
                            src={mediaSrc(msg)}
                            alt="Sticker"
                            className="max-w-[80px] sm:max-w-[100px] h-auto cursor-zoom-in"
                            onClick={() => setLightboxSrc(mediaSrc(msg))}
                          />
                        )}
                        {msg.messageType === 'document' && msg.telegramFilePath && (
                          <a href={mediaSrc(msg)} target="_blank" rel="noreferrer" className={`underline text-sm ${isOperator ? 'text-primary-foreground' : 'text-primary'}`}>
                            📄 {msg.fileName || 'Документ'} {msg.fileSize ? `(${(msg.fileSize / 1024).toFixed(2)} KB)` : ''}
                          </a>
                        )}
                        {msg.messageType === 'audio' && msg.telegramFilePath ? (
                          <audio controls src={mediaSrc(msg)} className="w-full mb-2" />
                        ) : null}
                        {msg.messageType === 'voice' && msg.telegramFilePath ? (
                          <audio controls src={mediaSrc(msg)} className="w-full mb-2" />
                        ) : null}
                        {msg.messageType === 'video' && msg.telegramFilePath && (
                          <video controls src={mediaSrc(msg)} className="w-full mb-2 max-h-60 sm:max-h-80" />
                        )}
                        {/* Caption / text body. Render content for ANY message
                            type that has it — previously this branch only
                            triggered for messageType==='text', so a photo +
                            подпись rendered the image alone and silently
                            dropped the caption. */}
                        {msg.content && (
                          <div className="text-sm whitespace-pre-wrap break-words">{msg.content}</div>
                        )}
                        <div className={`text-[10px] mt-1 text-right ${isOperator ? 'opacity-80' : 'text-muted-foreground'}`}>
                          {msg.timestamp
                            ? (chatMode
                                ? new Date(msg.timestamp).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })
                                : new Date(msg.timestamp).toLocaleString('ru-RU'))
                            : ''}
                        </div>
                      </div>
                    </div>
                    )}
                    </React.Fragment>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>
  );
  const composerBody = (
              <div className={chatMode ? 'border-t border-border px-3 py-2 bg-card' : 'mt-4 border-t border-border pt-4'}>
                {/* Paste-preview: confirm the screenshot before it's sent. */}
                {pendingImage && normalizeStatus(ticket.status || '') !== 'closed' && (
                  <div className="mb-3 rounded-lg border border-border bg-muted/40 p-3">
                    <div className="flex items-start gap-3">
                      <img
                        src={pendingImage.url}
                        alt="preview"
                        className="h-28 w-28 object-cover rounded-md border border-border shrink-0"
                      />
                      <div className="flex-1 min-w-0 space-y-2">
                        <p className="text-xs text-muted-foreground">
                          Проверьте изображение перед отправкой
                        </p>
                        <Input
                          type="text"
                          value={pendingCaption}
                          onChange={(e) => setPendingCaption(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); sendPendingImage(); } }}
                          placeholder="Подпись (необязательно)…"
                          className="w-full"
                          autoFocus
                        />
                        <div className="flex gap-2">
                          <Button
                            type="button"
                            size="sm"
                            onClick={sendPendingImage}
                            disabled={sendingMessage}
                          >
                            {sendingMessage ? 'Отправка…' : 'Отправить'}
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            onClick={cancelPendingImage}
                            disabled={sendingMessage}
                          >
                            Отмена
                          </Button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {normalizeStatus(ticket.status || '') === 'closed' ? (
                  /* Closed tickets are read-only — backend rejects sends too. */
                  <div className="flex items-center justify-center gap-2 py-3 text-sm text-muted-foreground bg-muted/40 rounded-md">
                    🔒 Заявка закрыта — переписка завершена, отправка сообщений недоступна.
                  </div>
                ) : (
                  <form onSubmit={handleSendMessage} className="flex flex-col sm:flex-row sm:items-end space-y-3 sm:space-y-0 sm:space-x-4">
                    <textarea
                      ref={textareaRef}
                      value={message}
                      onChange={(e) => { setMessage(e.target.value); autosize(); }}
                      onPaste={handlePaste}
                      onKeyDown={(e) => {
                        // Enter sends, Shift+Enter inserts a newline (paragraph).
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage(e);
                        }
                      }}
                      placeholder="Введите сообщение… (Shift+Enter — новая строка)"
                      rows={3}
                      className="flex-1 resize-none min-h-[76px] max-h-[220px] overflow-y-auto rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    />
                    <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFileChange} />
                    <div className="flex space-x-2 sm:space-x-2">
                      <Button
                        type="button"
                        onClick={handleAttachClick}
                        variant="secondary"
                        size="sm"
                        className="flex-1 sm:flex-none"
                      >
                        Прикрепить
                      </Button>
                      <Button
                        type="submit"
                        variant="default"
                        size="sm"
                        className="flex-1 sm:flex-none"
                        disabled={sendingMessage || !message.trim()}
                      >
                        {sendingMessage ? 'Отправка...' : 'Отправить'}
                      </Button>
                    </div>
                  </form>
                )}
              </div>
  );

  if (chatMode) {
    return (
      <div className="flex flex-col h-full min-h-0" onDrop={handleDrop} onDragOver={preventDefault}>
        {activeTab === 'internal' ? (
          <div className="flex-1 min-h-0 overflow-y-auto p-3">
            <InternalChat ticketId={ticket.id} supportLine={ticket.supportLine || 1} />
          </div>
        ) : feedBody}
        {activeTab !== 'internal' && composerBody}
        {lightboxSrc && (
          <div className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center p-4 cursor-zoom-out" onClick={() => setLightboxSrc(null)} role="dialog" aria-modal="true">
            <img src={lightboxSrc} alt="full size" className="max-h-[92vh] max-w-[92vw] object-contain rounded shadow-2xl" onClick={(e) => e.stopPropagation()} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={embedded ? 'space-y-3' : 'space-y-6'}>
      {/* Backward-compatible switch into the new Workspace (left-rail) view.
          Hidden when already embedded inside it. The classic page stays the
          default; this is an opt-in shortcut. */}
      {!embedded && (
        <div className="flex justify-end -mb-2">
          <Link
            to={`/work/${id}`}
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
            title="Открыть в новом рабочем окне со списком заявок слева"
          >
            <ChatBubbleLeftRightIcon className="h-3.5 w-3.5" />
            Рабочее окно (бета) →
          </Link>
        </div>
      )}
      {/* COMPACT messenger-style header — used only inside the Workspace
          (embedded). Replaces the bulky full card with a slim action bar +
          condensed body so the chat below gets the room. The classic card
          (rendered when !embedded) is left exactly as it was. */}
      {embedded && (
        <div className="bg-card border border-border rounded-lg overflow-hidden">
          <div className="flex items-start gap-3 px-3 sm:px-4 py-2.5 border-b border-border">
            <span
              className={`mt-1.5 w-2.5 h-2.5 rounded-full shrink-0 ${
                ({
                  new: 'bg-rose-500',
                  assigned: 'bg-blue-500',
                  in_progress: 'bg-emerald-500',
                  paused: 'bg-amber-500',
                  resolved: 'bg-emerald-600',
                  closed: 'bg-muted-foreground',
                } as Record<string, string>)[normalizeStatus(ticket.status || '')] || 'bg-muted-foreground'
              }`}
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 min-w-0 flex-wrap">
                <Link
                  to={`/clients/${encodeURIComponent(ticket.telegramChatId)}`}
                  className="text-sm sm:text-base font-semibold text-foreground hover:text-primary transition-colors truncate"
                  title="Открыть карточку клиента"
                >
                  {ticket.telegramChatType === 'private'
                    ? (ticket.telegramUsername
                        ? `@${ticket.telegramUsername}`
                        : `${ticket.telegramFirstName || ''} ${ticket.telegramLastName || ''}`.trim())
                    : ticket.telegramChatTitle || 'Группа'}
                </Link>
                <StatusBadge status={ticket.status || 'new'} />
              </div>
              <div className="text-[11px] text-muted-foreground truncate mt-0.5">
                #{ticket.ticketNumber} · Линия {ticket.supportLine || 1} · {new Date(ticket.createdAt).toLocaleString('ru-RU')}
                {ticket.assignedTo
                  ? ` · ${ticket.assignedTo.firstName} ${ticket.assignedTo.lastName}`
                  : ' · не назначен'}
              </div>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-1.5 shrink-0">
              <Button
                onClick={() => handleStatusChange('paused')}
                variant="default"
                size="sm"
                className="bg-warning-500 hover:bg-warning-600"
                disabled={statusUpdating || normalizeStatus(ticket.status || '') === 'paused'}
              >
                Пауза
              </Button>
              <Button
                onClick={() => handleStatusChange('in_progress')}
                variant="default"
                size="sm"
                disabled={statusUpdating || ['in_progress', 'in-progress'].includes(normalizeStatus(ticket.status || ''))}
              >
                Возобновить
              </Button>
              <Button
                onClick={() => handleStatusChange('resolved')}
                variant="default"
                size="sm"
                className="bg-emerald-600 hover:bg-emerald-700"
                disabled={statusUpdating || ['resolved', 'closed'].includes(normalizeStatus(ticket.status || ''))}
              >
                {statusUpdating ? '…' : 'Решено'}
              </Button>
              {/* Escalation as a normal small button, inline with the others —
                  not a giant full-width bar. Expands its form below the row. */}
              {ticket.supportLine === 1
                && !['resolved', 'closed'].includes(normalizeStatus(ticket.status || '')) && (
                <EscalationPanel
                  compact
                  ticketId={ticket.id}
                  currentSupportLine={ticket.supportLine || 1}
                  onEscalated={() => fetchTicket(id!)}
                />
              )}
              {(user?.role === 'admin' || user?.role === 'superadmin') && (
                <Button
                  onClick={() => handleStatusChange('closed')}
                  variant="secondary"
                  size="sm"
                  disabled={statusUpdating || normalizeStatus(ticket.status || '') === 'closed'}
                >
                  Закрыть
                </Button>
              )}
              {(user?.role === 'admin' || user?.role === 'superadmin') && (
                <Button onClick={handleDelete} variant="destructive" size="sm">
                  Удалить
                </Button>
              )}
            </div>
          </div>

          {/* Condensed body: label, description, resolution, CSAT (admin), escalation. */}
          <div className="px-3 sm:px-4 py-3 space-y-3">
            <div>{gameInstallChip}</div>
            {ticket.description && (
              <p className="text-sm text-foreground whitespace-pre-wrap">
                <span className="text-xs font-medium text-muted-foreground mr-1.5">Описание:</span>
                {ticket.description}
              </p>
            )}
            {['resolved', 'closed'].includes(normalizeStatus(ticket.status || '')) && (
              <div className="p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                <div className="flex items-baseline justify-between gap-3 mb-1">
                  <h3 className="text-xs font-medium text-emerald-700 dark:text-emerald-400">✅ Решение</h3>
                  {((ticket as any).resolvedBy || (ticket as any).resolutionTime) && (
                    <span className="text-[11px] text-muted-foreground">
                      {(ticket as any).resolvedBy
                        ? `${(ticket as any).resolvedBy.firstName || ''} ${(ticket as any).resolvedBy.lastName || ''}`.trim()
                        : ''}
                      {(ticket as any).resolutionTime
                        ? ` · ${new Date((ticket as any).resolutionTime).toLocaleString('ru-RU')}`
                        : ''}
                    </span>
                  )}
                </div>
                {(ticket as any).resolutionNote ? (
                  <p className="text-sm text-foreground whitespace-pre-wrap">{(ticket as any).resolutionNote}</p>
                ) : (
                  <p className="text-sm italic text-muted-foreground">Решение не зафиксировано.</p>
                )}
              </div>
            )}
            {user?.role !== 'operator' && <CsatBadge csat={(ticket as any).csat} />}
          </div>
        </div>
      )}

      {!embedded && (
      <div className="bg-card border border-border shadow rounded-lg p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start space-y-4 sm:space-y-0">
          <div className="flex-1">
            <h1 className="text-xl sm:text-2xl font-bold text-foreground">
              Заявка #{ticket.ticketNumber}
            </h1>
            <p className="text-base sm:text-lg text-muted-foreground mt-2">{ticket.subject}</p>
            <div className="mt-2">{gameInstallChip}</div>
            {ticket.description && (
              <div className="mt-3 p-3 sm:p-4 bg-primary/10 border border-primary/20 rounded-lg">
                <h3 className="text-sm font-medium text-primary mb-2">📝 Описание проблемы:</h3>
                <p className="text-sm text-foreground whitespace-pre-wrap">{ticket.description}</p>
              </div>
            )}
            {/* Canonical resolution — operator's "что было сделано" comment
                captured on the resolve transition. We render it whenever the
                ticket has passed the resolved gate (resolved or closed). For
                legacy tickets without a note we still render the block but
                with a muted placeholder, so the absence is explicit, not
                silently missing. */}
            {['resolved', 'closed'].includes(normalizeStatus(ticket.status || '')) && (
              <div className="mt-3 p-3 sm:p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
                <div className="flex items-baseline justify-between gap-3 mb-2">
                  <h3 className="text-sm font-medium text-emerald-700 dark:text-emerald-400">
                    ✅ Решение:
                  </h3>
                  {((ticket as any).resolvedBy || (ticket as any).resolutionTime) && (
                    <span className="text-[11px] text-muted-foreground">
                      {(ticket as any).resolvedBy
                        ? `${(ticket as any).resolvedBy.firstName || ''} ${(ticket as any).resolvedBy.lastName || ''}`.trim()
                        : ''}
                      {(ticket as any).resolutionTime
                        ? ` · ${new Date((ticket as any).resolutionTime).toLocaleString('ru-RU')}`
                        : ''}
                    </span>
                  )}
                </div>
                {(ticket as any).resolutionNote ? (
                  <p className="text-sm text-foreground whitespace-pre-wrap">
                    {(ticket as any).resolutionNote}
                  </p>
                ) : (
                  <p className="text-sm italic text-muted-foreground">
                    Решение не зафиксировано (заявка закрыта до внедрения функции).
                  </p>
                )}
              </div>
            )}
            {/* CSAT score is hidden from operators — they must not see how
                the client rated their work. Admins/superadmins see it.
                Backend also blanks the rating for operators (defence in depth). */}
            {user?.role !== 'operator' && <CsatBadge csat={(ticket as any).csat} />}
          </div>
          <div className="flex flex-wrap gap-2 sm:gap-2">
            <Button
              onClick={() => handleStatusChange('paused')}
              variant="default"
              size="sm"
              className="bg-warning-500 hover:bg-warning-600"
              disabled={statusUpdating || normalizeStatus(ticket.status || '') === 'paused'}
            >
              Пауза
            </Button>
            <Button
              onClick={() => handleStatusChange('in_progress')}
              variant="default"
              size="sm"
              disabled={statusUpdating || ['in_progress', 'in-progress'].includes(normalizeStatus(ticket.status || ''))}
            >
              Возобновить
            </Button>
            {/* Primary "done" action — fires CSAT prompt to the client.
                The system auto-flips the ticket to 'closed' once the client
                rates or 72h pass (server-side beat task). */}
            <Button
              onClick={() => handleStatusChange('resolved')}
              variant="default"
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700"
              disabled={statusUpdating || ['resolved', 'closed'].includes(normalizeStatus(ticket.status || ''))}
            >
              {statusUpdating ? 'Обработка…' : 'Решено'}
            </Button>
            {/* Force-close stays admin-only — used for spam/dup/no-client cases
                where we don't want to send a CSAT prompt at all. */}
            {(user?.role === 'admin' || user?.role === 'superadmin') && (
              <Button
                onClick={() => handleStatusChange('closed')}
                variant="secondary"
                size="sm"
                disabled={statusUpdating || normalizeStatus(ticket.status || '') === 'closed'}
              >
                Закрыть
              </Button>
            )}
            {(user?.role === 'admin' || user?.role === 'superadmin') && (
              <Button
                onClick={handleDelete}
                variant="destructive"
                size="sm"
              >
                Удалить
              </Button>
            )}
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
          {/* Клиент-плашка кликабельная — провал в карточку клиента
              с CSAT/NPS/health-score. Под капотом это переход к
              /clients/<telegramChatId>, ключу, по которому строится вся
              аналитика клиента (в текущей модели «клиент = TG-чат»). */}
          <Link
            to={`/clients/${encodeURIComponent(ticket.telegramChatId)}`}
            className="block rounded-md -mx-1 px-1 py-0.5 hover:bg-accent transition-colors group"
            title="Открыть карточку клиента"
          >
            <p className="text-sm text-muted-foreground flex items-center gap-1">
              Клиент
              <span className="text-[10px] text-primary opacity-0 group-hover:opacity-100 transition-opacity">
                открыть →
              </span>
            </p>
            <p className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
              {ticket.telegramChatType === 'private'
                ? (ticket.telegramUsername
                    ? `@${ticket.telegramUsername}`
                    : `${ticket.telegramFirstName || ''} ${ticket.telegramLastName || ''}`.trim())
                : ticket.telegramChatTitle || 'Группа'
              }
            </p>
            <p className="text-xs text-muted-foreground">ID: {ticket.telegramUserId}</p>
          </Link>
          <div>
            <p className="text-sm text-muted-foreground">Создано</p>
            <p className="text-sm font-medium text-foreground">
              {new Date(ticket.createdAt).toLocaleString('ru-RU')}
            </p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Статус</p>
            <div className="mt-1">
              <StatusBadge status={ticket.status || 'new'} />
            </div>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">Оператор</p>
            <p className="text-sm font-medium text-foreground">
              {ticket.assignedTo ? 
                `${ticket.assignedTo.firstName} ${ticket.assignedTo.lastName}` : 
                'Не назначен'}
            </p>
          </div>
          {ticket.supportLine && (
            <div>
              <p className="text-sm text-muted-foreground">Линия поддержки</p>
              <p className="text-sm font-medium text-foreground">
                Линия {ticket.supportLine}
              </p>
            </div>
          )}
        </div>

        {/* Панель эскалации — только для активных заявок 1-й линии.
            На resolved/closed эскалировать нечего, и кнопка только сбивает с толку. */}
        {ticket.supportLine === 1
          && !['resolved', 'closed'].includes(normalizeStatus(ticket.status || '')) && (
          <div className="mt-6">
            <EscalationPanel
              ticketId={ticket.id}
              currentSupportLine={ticket.supportLine || 1}
              onEscalated={() => fetchTicket(id!)}
            />
          </div>
        )}
      </div>
      )}

      {/* Вкладки */}
      <div className="bg-card border border-border rounded-lg">
        <div className="border-b border-border">
          <div className="flex overflow-x-auto">
            <button
              onClick={() => setActiveTab('messages')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'messages'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              Сообщения
            </button>
            <button
              onClick={() => setActiveTab('internal')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'internal'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <ChatBubbleLeftRightIcon className="h-4 w-4 inline mr-2" />
              Внутренняя переписка
            </button>
            <button
              onClick={() => setActiveTab('transfers')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'transfers'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <ArrowPathIcon className="h-4 w-4 inline mr-2" />
              История передач
            </button>
            <button
              onClick={() => setActiveTab('worktime')}
              className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'worktime'
                  ? 'border-primary text-primary'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <ClockIcon className="h-4 w-4 inline mr-2" />
              Время работы
            </button>
          </div>
        </div>

        <div className="p-4 sm:p-6">
          {/* Вкладка сообщений */}
          {activeTab === 'messages' && (
            <div>
              <div className="mb-4">
                <h2 className="text-base sm:text-lg font-medium text-foreground">История сообщений</h2>
              </div>
              
                {feedBody}

                {composerBody}
            </div>
          )}

          {/* Вкладка внутренней переписки */}
          {activeTab === 'internal' && (
            <InternalChat
              ticketId={ticket.id}
              supportLine={ticket.supportLine || 1}
            />
          )}

          {/* Вкладка истории передач */}
          {activeTab === 'transfers' && (
            <div>
              <h2 className="text-base sm:text-lg font-medium text-foreground mb-4">
                История передач заявки
              </h2>
              {ticket.transferHistory && ticket.transferHistory.length > 0 ? (
                <div className="space-y-4">
                  {ticket.transferHistory.map((transfer: any, index: number) => (
                    <div key={index} className="border border-border rounded-lg p-4 bg-muted/30">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-sm font-medium text-foreground">
                          Передача #{index + 1}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {new Date(transfer.transferredAt).toLocaleString('ru-RU')}
                        </div>
                      </div>
                      <div className="text-sm text-foreground space-y-1">
                        <div>
                          <span className="text-muted-foreground">От:</span>{' '}
                          {transfer.fromOperator?.firstName || 'Неизвестно'} {transfer.fromOperator?.lastName || ''}
                        </div>
                        <div>
                          <span className="text-muted-foreground">К:</span>{' '}
                          {transfer.toOperator?.firstName || 'Неизвестно'} {transfer.toOperator?.lastName || ''}
                        </div>
                        {transfer.reason && (
                          <div>
                            <span className="text-muted-foreground">Причина:</span> {transfer.reason}
                          </div>
                        )}
                        {transfer.comment && (
                          <div className="mt-2 p-2 bg-background rounded text-xs">
                            {transfer.comment}
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <ArrowPathIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Нет истории передач</p>
                </div>
              )}
            </div>
          )}

          {/* Вкладка времени работы */}
          {activeTab === 'worktime' && (
            <div>
              <h2 className="text-base sm:text-lg font-medium text-foreground mb-4">
                Метрики времени работы над заявкой
              </h2>
              
              {workTimeLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
                  <p className="text-sm text-muted-foreground">Загрузка метрик...</p>
                </div>
              ) : workTimeMetrics ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <div className="border border-border rounded-lg p-4 bg-muted/30">
                      <div className="text-xs text-muted-foreground mb-1">Общее время работы</div>
                      <div className="text-lg font-semibold text-foreground">
                        {formatDuration(workTimeMetrics.totalWorkTime || 0)}
                      </div>
                    </div>
                    <div className="border border-border rounded-lg p-4 bg-muted/30">
                      <div className="text-xs text-muted-foreground mb-1">Текущее время работы</div>
                      <div className="text-lg font-semibold text-foreground">
                        {formatDuration(workTimeMetrics.currentWorkTime || 0)}
                      </div>
                    </div>
                    <div className="border border-border rounded-lg p-4 bg-muted/30">
                      <div className="text-xs text-muted-foreground mb-1">Общее время пауз</div>
                      <div className="text-lg font-semibold text-foreground">
                        {formatDuration(workTimeMetrics.totalPauseTime || 0)}
                      </div>
                    </div>
                  </div>

                  {/* Read-only "is the clock running?" indicator. The clock
                      is now driven by the ticket status itself — entering
                      `in_progress` (Возобновить / Взять в работу) starts it,
                      Пауза / Решено / Закрыть stop it. The old explicit
                      "Начать работу / Пауза / Продолжить" buttons here were
                      duplicating those header actions and were removed. */}
                  {workTimeMetrics.isWorking && (
                    <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
                      <div className="h-2 w-2 bg-green-500 rounded-full animate-pulse"></div>
                      Работа идёт — таймер запускается статусом «в работе»
                    </div>
                  )}

                  {workTimeMetrics.pauseHistory && workTimeMetrics.pauseHistory.length > 0 && (
                    <div>
                      <h3 className="text-sm font-medium text-foreground mb-2">История пауз</h3>
                      <div className="space-y-2">
                        {workTimeMetrics.pauseHistory.map((pause: any, index: number) => (
                          <div key={index} className="border border-border rounded p-3 text-sm">
                            <div className="flex justify-between items-start mb-1">
                              <div>
                                <div className="font-medium text-foreground">
                                  Пауза #{index + 1}
                                </div>
                                {pause.reason && (
                                  <div className="text-xs text-muted-foreground mt-1">
                                    Причина: {pause.reason}
                                  </div>
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground">
                                {formatDuration(pause.duration || 0)}
                              </div>
                            </div>
                            <div className="text-xs text-muted-foreground">
                              Начало: {new Date(pause.startedAt).toLocaleString('ru-RU')}
                              {pause.endedAt ? (
                                <> | Конец: {new Date(pause.endedAt).toLocaleString('ru-RU')}</>
                              ) : (
                                <> | <span className="text-orange-500">В процессе</span></>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <ClockIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                  <p>Метрики времени работы недоступны</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Lightbox overlay — fullscreen view for photo/sticker bubbles.
          Click anywhere or press Esc to close. We intentionally keep this
          dependency-free (no library) — single state + portal-like fixed div. */}
      {lightboxSrc && (
        <div
          className="fixed inset-0 z-[100] bg-black/85 flex items-center justify-center p-4 cursor-zoom-out"
          onClick={() => setLightboxSrc(null)}
          role="dialog"
          aria-modal="true"
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightboxSrc(null); }}
            className="absolute top-4 right-4 h-10 w-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center text-xl"
            aria-label="Закрыть"
          >
            ✕
          </button>
          <img
            src={lightboxSrc}
            alt="full size"
            className="max-h-[92vh] max-w-[92vw] object-contain rounded shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};


export default TicketDetailPage;